import React from 'react';
import { useTranslation } from 'react-i18next';
import './SortableTable.css';

const SortableTable = React.memo(({ 
    columns, 
    data, 
    onEdit, 
    onDelete, 
    onSpecialAction,
    specialActionText,
    getRowClassName,
    emptyMessage = "No data available",
    // Sorting props
    sortField,
    sortOrder,
    onSort
}) => {
    const { t } = useTranslation(['common']);

    const handleColumnClick = (column) => {
        if (!column.sortable || !onSort) return;
        
        const newOrder = (sortField === column.key && sortOrder === 'asc') ? 'desc' : 'asc';
        onSort(column.key, newOrder);
    };

    const getSortIcon = (column) => {
        if (!column.sortable || sortField !== column.key) {
            return column.sortable ? <span className="sort-icon neutral">⇕</span> : null;
        }
        
        return (
            <span className={`sort-icon ${sortOrder}`}>
                {sortOrder === 'asc' ? '↑' : '↓'}
            </span>
        );
    };

    return (
        <div className="table-container">
            <table className="data-table sortable-table">
                <thead>
                    <tr>
                        {columns.map(column => (
                            <th 
                                key={column.key}
                                className={`${column.sortable ? 'sortable' : ''} ${sortField === column.key ? 'sorted' : ''}`}
                                onClick={() => handleColumnClick(column)}
                                title={column.sortable ? t('common:clickToSort') : undefined}
                            >
                                <div className="column-header">
                                    <span className="column-label">{column.label}</span>
                                    {getSortIcon(column)}
                                </div>
                            </th>
                        ))}
                        {(onEdit || onDelete || onSpecialAction) && (
                            <th className="actions-header">{t('common:actions')}</th>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {data.length === 0 ? (
                        <tr>
                            <td 
                                colSpan={columns.length + (onEdit || onDelete || onSpecialAction ? 1 : 0)} 
                                className="text-center no-data"
                            >
                                {emptyMessage}
                            </td>
                        </tr>
                    ) : (
                        data.map(item => (
                            <tr key={item.id} className={getRowClassName ? getRowClassName(item) : ''}>
                                {columns.map(column => (
                                    <td key={column.key}>
                                        {column.render ? column.render(item) : item[column.key]}
                                    </td>
                                ))}
                                {(onEdit || onDelete || onSpecialAction) && (
                                    <td className="actions">
                                        {onEdit && (
                                            <button 
                                                onClick={() => onEdit(item)} 
                                                className="btn btn-sm btn-primary"
                                                title={t('common:edit')}
                                            >
                                                {t('common:edit')}
                                            </button>
                                        )}
                                        {onSpecialAction && (
                                            <button 
                                                onClick={() => onSpecialAction(item)} 
                                                className="btn btn-sm btn-secondary"
                                                title={specialActionText}
                                            >
                                                {specialActionText}
                                            </button>
                                        )}
                                        {onDelete && (
                                            <button 
                                                onClick={() => onDelete(item)} 
                                                className="btn btn-sm btn-danger"
                                                title={t('common:delete')}
                                            >
                                                {t('common:delete')}
                                            </button>
                                        )}
                                    </td>
                                )}
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
});

export default SortableTable;